import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { queryKeys } from "@shared/api/queryKeys";
import { getCurrentUser } from "@shared/api/endpoints/auth";

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
    </div>
  );
}

// Layout route: renders child routes only when authenticated.
// Redirects to /login?next=<current-path> on any auth failure (401 or 403).
// Note: DRF returns 403 (not 401) when a stale/invalid session cookie is present,
// so we treat both as "not logged in" here.
export default function RequireAuth() {
  const { isLoading, isError, error } = useQuery({
    queryKey: queryKeys.auth.user(),
    queryFn: getCurrentUser,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isError) return;
    const status = (error as AxiosError)?.response?.status;
    if (status === 401 || status === 403) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
    }
  }, [isError, error]);

  if (isLoading) return <FullPageSpinner />;
  if (isError) return null; // redirect is in flight

  return <Outlet />;
}
